// Chat uses the console session and project; response cost comes from its request receipt.
// The displayed code and dispatched request share the same form values.
import { ApiError, classify, moneyAction, NetworkError } from "@nodus/api";
import {
  ActionLink,
  Advanced,
  Button,
  Callout,
  CodeSnippet,
  CopyButton,
  Field,
  formatCount,
  Input,
  Markdown,
  Textarea,
} from "@nodus/ui";
import { Code2, ArrowUp } from "lucide-react";
import * as React from "react";
import { bodyBytes, MAX_CHAT_BODY, sendChat, type ChatOutcome } from "./chat";
import { useInferenceBaseURL, useInferenceClient, useReceiptLookup } from "./data";
import {
  CAPABILITIES,
  CAPABILITY_LABEL,
  cardPriceLabel,
  compareModels,
  isRouted,
  modelKind,
  modelTitle,
  tokenCountLabel,
  usd,
  type CatalogModel,
  type Receipt,
  type Scope,
} from "./model";
import { selectClass } from "./decision-questions";
import { chatSnippets, embeddingSnippets, transcriptionSnippet, type ChatRequest } from "./snippets";


const EXAMPLE = "";
const DEFAULT_TEMPERATURE = "0.7";
const DEFAULT_MAX_TOKENS = 2048;
/** How often and how many times the receipt is read while the request is still settling. */
const RECEIPT_POLL = { attempts: 5, everyMs: 700 } as const;

type Run =
  | { state: "idle" }
  | { state: "running"; outcome: ChatOutcome }
  | { state: "done"; outcome: ChatOutcome; stopped?: boolean; receipt?: Receipt }
  | { state: "error"; error: unknown; outcome?: ChatOutcome };

/** The model line under the select: what it costs and what it can do. */
function modelFacts(m: CatalogModel): string | undefined {
  if (isRouted(m.id)) return undefined;
  const caps = CAPABILITIES.filter((c) => m.capabilities?.[c]).map((c) => CAPABILITY_LABEL[c]);
  const p = m.pricing;
  const price = p?.input && p.output ? `$${Number(p.input)} in / $${Number(p.output)} out per 1M tokens` : cardPriceLabel(m);
  return [m.context_window ? `${tokenCountLabel(m.context_window)} context` : "", price ?? "", ...caps].filter(Boolean).join(" · ");
}

function ms(n: number | undefined): string {
  if (n === undefined) return "—";
  return n < 1000 ? `${Math.round(n)} ms` : `${(n / 1000).toFixed(2)} s`;
}

function wait(msec: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve) => {
    const t = setTimeout(resolve, msec);
    signal.addEventListener("abort", () => (clearTimeout(t), resolve()), { once: true });
  });
}

/** Stream / Non-stream, as two pressed-state buttons. */
function ModeToggle({ stream, onChange }: { stream: boolean; onChange: (stream: boolean) => void }) {
  return (
    <div className="grid gap-1">
      <span id="pg-mode" className="text-small font-medium text-fg-secondary">
        Mode
      </span>
      <div role="group" aria-labelledby="pg-mode" className="flex h-[var(--nd-control-height)] rounded-md border border-line-control p-0.5">
        {(
          [
            [true, "Stream"],
            [false, "Non-stream"],
          ] as const
        ).map(([value, label]) => (
          <button
            key={label}
            type="button"
            aria-pressed={stream === value}
            onClick={() => onChange(value)}
            className="flex-1 rounded-sm px-2 text-ui whitespace-nowrap text-fg-muted hover:text-fg-primary aria-pressed:bg-selected aria-pressed:text-fg-primary"
          >
            {label}
          </button>
        ))}
      </div>
    </div>
  );
}

/** The API's message with its fix, inline under the request. */
export function RunError({ error, scope }: { error: unknown; scope: Scope }) {
  if (error instanceof NetworkError) {
    return (
      <Callout tone="danger" role="alert" title="Can't reach the API">
        Check your connection and send again.
      </Callout>
    );
  }
  if (!(error instanceof ApiError)) {
    return <Callout tone="danger" role="alert" title="The answer could not be read">{error instanceof Error ? error.message : String(error)}</Callout>;
  }
  const c = classify(error);
  const money = c === "money" ? moneyAction(error, scope) : undefined;
  const members = `/${encodeURIComponent(scope.org)}/team/members`;
  const fix: Partial<Record<typeof c, string>> = {
    unauthorized: "Your session ended. Sign in again, then send the request again.",
    forbidden: "Sending requests needs the Member role or above in this organization. Ask an Owner or Admin.",
    money:
      error.reason === "BudgetExceeded"
        ? "Raise the budget, or lower Max tokens: the hold covers the request's maximum cost."
        : "Add credits, or lower Max tokens: the hold covers the request's maximum cost.",
    quota: "Choose a model by name to keep going; it is billed per token from your credits.",
    rateLimited: `Send again in ${error.retryAfterSeconds ?? 5} s.`,
  };
  const hint = error.status.fix ?? fix[c] ?? (error.code >= 500 ? "Send again shortly; a failed request is not charged." : undefined);
  const action = money ? (
    <ActionLink href={money.href}>{money.label}</ActionLink>
  ) : c === "forbidden" ? (
    <ActionLink href={members}>Team</ActionLink>
  ) : undefined;
  return (
    <Callout tone={c === "money" || c === "quota" || c === "rateLimited" ? "warning" : "danger"} role="alert" title={error.status.message ?? `Request failed (${error.code})`} action={action}>
      {hint ? <p>{hint}</p> : null}
      <p className="mt-1 font-mono text-small text-fg-muted">
        {error.code} {error.reason ?? ""}
        {error.requestId ? ` · ${error.requestId}` : ""}
      </p>
    </Callout>
  );
}

/** Requested model, tier, tokens, latency, cost and the request id, once the answer has ended. */
function RunFacts({ outcome, receipt }: { outcome: ChatOutcome; receipt?: Receipt | undefined }) {
  const inTokens = outcome.usage?.prompt_tokens ?? receipt?.usage?.inputTokens;
  const outTokens = outcome.usage?.completion_tokens ?? receipt?.usage?.outputTokens;
  const cost = !receipt
    ? "—"
    : receipt.state === "Settled"
      ? usd(receipt.amountUSD)
      : receipt.state === "Running"
        ? `${usd(receipt.heldUSD)} held, settling`
        : receipt.state === "Released"
          ? "$0.00, released"
          : "—";
  const facts: [string, React.ReactNode][] = [
    ["Model", <code className="font-mono">{outcome.requestedModel}</code>],
    ...(outcome.tier ? ([["Tier", outcome.tier === "free" ? "Free" : outcome.tier === "paid" ? "Paid" : outcome.tier]] as [string, string][]) : []),
    ["Tokens", inTokens !== undefined || outTokens !== undefined ? `${formatCount(inTokens ?? 0)} in · ${formatCount(outTokens ?? 0)} out` : "—"],
    ["Latency", outcome.firstTokenMs !== undefined ? `${ms(outcome.totalMs)} (first token ${ms(outcome.firstTokenMs)})` : ms(outcome.totalMs)],
    ["Cost", cost],
  ];
  return (
    <div className="border-t border-line-subtle px-4 py-3">
      <dl aria-label="Request details" className="flex flex-wrap gap-x-5 gap-y-1.5 text-small">
        {facts.map(([label, value]) => (
          <div key={label} className="flex gap-1.5">
            <dt className="text-fg-muted">{label}</dt>
            <dd className="text-fg-primary tabular-nums">{value}</dd>
          </div>
        ))}
      </dl>
      {outcome.requestId ? (
        <div className="mt-1.5 flex items-center gap-1.5 text-small text-fg-muted">
          <span>Receipt</span>
          <code className="min-w-0 truncate font-mono text-fg-secondary">{outcome.requestId}</code>
          <CopyButton text={outcome.requestId} label="Copy request id" />
        </div>
      ) : null}
    </div>
  );
}

export function PlaygroundBody({ models, model, scope, onModelChange, navigation, onBusyChange }: { models: CatalogModel[]; model: CatalogModel; scope: Scope; onModelChange: (id: string) => void; navigation?: React.ReactNode; onBusyChange?: (busy: boolean) => void }) {
  const client = useInferenceClient();
  const lookup = useReceiptLookup(scope);
  const baseURL = useInferenceBaseURL();
  const [system, setSystem] = React.useState("");
  const [message, setMessage] = React.useState(EXAMPLE);
  const [temperature, setTemperature] = React.useState(DEFAULT_TEMPERATURE);
  const [maxTokens, setMaxTokens] = React.useState(String(Math.min(DEFAULT_MAX_TOKENS, model.max_output_tokens ?? DEFAULT_MAX_TOKENS)));
  const [stream, setStream] = React.useState(true);
  const [showCode, setShowCode] = React.useState(false);
  const [run, setRun] = React.useState<Run>({ state: "idle" });
  const abort = React.useRef<AbortController | undefined>(undefined);
  React.useEffect(() => () => abort.current?.abort(), []);

  const kind = modelKind(model);
  const t = Number(temperature);
  const n = Number(maxTokens);
  const cap = model.max_output_tokens;
  const problems = {
    temperature: temperature === "" || !Number.isFinite(t) || t < 0 || t > 2 ? "Use 0 to 2." : undefined,
    maxTokens:
      maxTokens === "" || !Number.isInteger(n) || n < 1 || (cap !== undefined && n > cap)
        ? `Use a whole number from 1 to ${formatCount(cap ?? 1_000_000)}.`
        : undefined,
  };
  const req: ChatRequest = {
    model: model.id,
    messages: [...(system.trim() ? [{ role: "system" as const, content: system }] : []), { role: "user" as const, content: message }],
    ...(problems.temperature ? {} : { temperature: t }),
    ...(problems.maxTokens ? {} : { max_tokens: n }),
    stream,
  };
  const tooLarge = bodyBytes(req) > MAX_CHAT_BODY;
  const running = run.state === "running";
  React.useEffect(() => { onBusyChange?.(running); return () => onBusyChange?.(false); }, [running, onBusyChange]);
  const canSend = kind === "chat" && !running && Boolean(message.trim()) && !problems.temperature && !problems.maxTokens && !tooLarge;

  const send = async () => {
    if (!canSend || abort.current) return;
    const ctl = new AbortController();
    abort.current = ctl;
    setRun({ state: "running", outcome: { requestedModel: req.model, text: "", reasoning: "" } });
    let last: ChatOutcome | undefined;
    try {
      last = await sendChat(client, scope, req, (o) => ((last = o), setRun({ state: "running", outcome: o })), ctl.signal);
    } catch (e) {
      if ((e as { name?: string }).name === "AbortError") {
        setRun({ state: "done", outcome: last ?? { requestedModel: req.model, text: "", reasoning: "" }, stopped: true });
      } else {
        setRun({ state: "error", error: e, ...(last ? { outcome: last } : {}) });
      }
      if (abort.current === ctl) abort.current = undefined;
      return;
    }
    const outcome = last;
    setRun({ state: "done", outcome });
    if (!outcome.requestId) { if (abort.current === ctl) abort.current = undefined; return; }
    for (let i = 0; i < RECEIPT_POLL.attempts && !ctl.signal.aborted; i++) {
      const receipt = await lookup(outcome.requestId).catch(() => undefined);
      if (ctl.signal.aborted) { if (abort.current === ctl) abort.current = undefined; return; }
      if (receipt) setRun({ state: "done", outcome, receipt });
      if (!receipt || receipt.state !== "Running") break;
      await wait(RECEIPT_POLL.everyMs, ctl.signal);
    }
    if (abort.current === ctl) abort.current = undefined;
  };


  const options = [...models].sort(compareModels).map((m) => ({ value: m.id, label: isRouted(m.id) ? `Automatic · ${modelTitle(m)} (${m.id})` : `${modelTitle(m)} (${m.id})` }));
  const outcome = run.state === "idle" ? undefined : run.outcome;
  const snippets = kind === "chat" ? chatSnippets(req, baseURL) : kind === "embeddings" ? embeddingSnippets(model.id, baseURL) : kind === "transcription" ? [transcriptionSnippet(model.id, baseURL)] : [];

  return <div className="flex min-h-0 flex-1 flex-col bg-subtle">
    <div className="flex shrink-0 flex-wrap items-center gap-3 border-b border-line-subtle px-5 py-3 sm:px-6">
      {navigation}
      <label htmlFor="pg-model" className="sr-only">Model</label><select id="pg-model" value={model.id} onChange={(e) => onModelChange(e.target.value)} disabled={running} className={`${selectClass} w-64 max-w-full bg-raised`}>{options.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}</select>
    </div>
    <div className="min-h-0 flex-1 overflow-y-auto px-5 py-8">
      <div className="mx-auto w-full max-w-4xl">
        {run.state === "idle" ? <div className="flex flex-col items-center py-16 text-center sm:py-24">
          <h2 className="text-3xl font-semibold tracking-tight text-fg-primary">Ask anything</h2>
          <p className="mt-4 max-w-lg text-ui leading-6 text-fg-muted">Try a model with your own prompt. See the response, token usage, latency, and cost from the same API your application uses.</p>
          <div className="mt-6 flex flex-wrap justify-center gap-2">{["Explain a concept", "Debug some code", "Summarize a document"].map((label, i) => <Button key={label} variant="outline" onClick={() => setMessage(["Explain how prefix caching works in simple terms.", "Help me diagnose a memory leak in a Python service.", "Summarize the following document:\n\n"][i]!)}>{label}</Button>)}</div>
        </div> : null}
          {run.state !== "idle" ? (
            <section aria-labelledby="pg-response" className="rounded-lg border border-line bg-sunken">
              <div className="flex items-center justify-between border-b border-line-subtle px-4 py-2">
                <h3 id="pg-response" className="text-small font-medium text-fg-secondary">
                  Response
                </h3>
                <span role="status" className="text-small text-fg-muted">
                  {running ? (stream ? "Streaming…" : "Waiting for the answer…") : run.state === "done" ? (run.stopped ? "Stopped" : "Done") : ""}
                </span>
              </div>
              {run.state === "error" ? (
                <div className="p-3">
                  <RunError error={run.error} scope={scope} />
                </div>
              ) : null}
              {outcome?.reasoning ? (
                <details className="border-b border-line-subtle px-4 py-2 text-small text-fg-muted">
                  <summary className="cursor-pointer select-none">Reasoning</summary>
                  <Markdown className="mt-1.5 text-small text-fg-muted">{outcome.reasoning}</Markdown>
                </details>
              ) : null}
              {run.state !== "error" || outcome?.text ? (
                <div aria-busy={running} data-testid="pg-answer" className="min-h-24 px-4 py-3">
                  <Markdown>{outcome?.text ?? ""}</Markdown>
                </div>
              ) : null}
              {run.state === "done" ? <RunFacts outcome={run.outcome} receipt={run.receipt} /> : null}
            </section>
          ) : null}

      </div>
    </div>
    <div className="max-h-[50vh] shrink-0 overflow-y-auto px-4 pb-5 sm:px-6">
      <div className="mx-auto max-w-4xl rounded-xl border border-line bg-raised p-4">
        <label htmlFor="pg-message" className="sr-only">Message</label>
        <Textarea id="pg-message" rows={3} className="min-h-24 resize-y border-0 bg-transparent px-0 text-[14px] shadow-none focus-visible:ring-0" value={message} placeholder="Ask anything…" disabled={running} onChange={(e) => setMessage(e.target.value)} />
        <div className="mt-3 flex flex-wrap items-center gap-3">
          <p className="mr-auto text-xs text-fg-muted">{modelFacts(model)}</p>
          <Button size="sm" variant="ghost" aria-pressed={showCode} onClick={() => setShowCode(!showCode)}><Code2 size={14} aria-hidden />Code</Button>
          {running ? <Button variant="outline" onClick={() => abort.current?.abort()}>Stop</Button> : <Button className="bg-fg-primary text-canvas hover:bg-fg-secondary" onClick={() => void send()} disabled={!canSend}>Send<ArrowUp size={15} aria-hidden /></Button>}
        </div>
        <Advanced label="Parameters"><fieldset disabled={running} className="grid gap-4 pt-3">
          <Field id="pg-system" label="System prompt"><Textarea rows={2} value={system} placeholder="You are a concise assistant." onChange={(e) => setSystem(e.target.value)} /></Field>
          <div className="grid gap-4 sm:grid-cols-3">
            <Field id="pg-temperature" label="Temperature" error={problems.temperature}><Input type="number" min={0} max={2} step={0.1} value={temperature} onChange={(e) => setTemperature(e.target.value)} /></Field>
            <Field id="pg-max-tokens" label="Max tokens" error={problems.maxTokens}><Input type="number" min={1} {...(cap ? { max: cap } : {})} step={1} value={maxTokens} onChange={(e) => setMaxTokens(e.target.value)} /></Field>
            <ModeToggle stream={stream} onChange={setStream} />
          </div>
        </fieldset></Advanced>
        {tooLarge ? <p role="alert" className="mt-2 text-small text-danger-fg">Over the API's 1 MiB limit.</p> : null}
        {showCode ? <div className="mt-3"><CodeSnippet variants={snippets} initial="curl" /></div> : null}
      </div>
      <p className="mx-auto mt-3 max-w-4xl text-center text-xs text-fg-muted">Billed to your organization at catalog prices. Stopping locally does not cancel requests already sent.</p>
    </div>
  </div>;
}
